import {Autocomplete, Button, TextField} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useToken} from '@/components/TokenContext';
import type {AccessibleResources, Project} from '@/types/atlassian';

const useQuery = () => {
    const {search} = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
};

type ProjectSelect = Project & {
    label: string;
};

const Home = (): JSX.Element => {
    const query = useQuery();
    const {token} = useToken();
    const [projects, setProjects] = useState<ProjectSelect[]>([]);
    const [selectedProject, setSelectedProject] = useState<ProjectSelect | null>(null);
    const [clicked, setClicked] = useState(false);
    const [status, setStatus] = useState('Loading...');
    const jiraBaseUrl = query.get('jiraBaseUrl') ?? '';

    // biome-ignore lint/correctness/useExhaustiveDependencies: use effect
    useEffect(() => {
        const getProjects = async () => {
            if (!token) {
                return;
            }

            const resourcesResponse = await fetch(
                'https://api.atlassian.com/oauth/token/accessible-resources',
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                },
            );

            const resources = (await resourcesResponse.json()) as AccessibleResources[];

            const jiraInstance = resources.find(r =>
                jiraBaseUrl.toLowerCase().startsWith(r.url.toLowerCase()),
            );

            if (!jiraInstance) {
                setStatus(
                    `Failed to Find Jira instance ${jiraBaseUrl} in accessible resources` +
                        ` ${resources.map(r => r.url).join(', ')}`,
                );
                return;
            }

            const response = await fetch(
                `https://api.atlassian.com/ex/jira/${jiraInstance.id}/rest/api/3/project`,
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                },
            );

            if (!response.ok) {
                setStatus('Failed to fetch projects.');
            }

            const projects = (await response.json()) as Project[];

            setProjects(
                projects.map(
                    p =>
                        ({
                            ...p,
                            label: `${p.key} - ${p.name}`,
                        }) satisfies ProjectSelect,
                ),
            );
            setStatus('');
        };

        if (token) {
            getProjects().catch(e => {
                setStatus('Failed to fetch projects.');
                console.error(e);
            });
        }
    }, [token]);

    return (
        <>
            {status}
            {projects.length > 0 && (
                <FormControl fullWidth>
                    <Autocomplete
                        disablePortal
                        id='combo-box-demo'
                        options={projects}
                        sx={{width: 300, pt: 3}}
                        onChange={(_event, value) => {
                            const project = projects.find(p => p.id === value?.id) ?? null;
                            setSelectedProject(project);
                        }}
                        renderInput={params => <TextField {...params} label='Project' />}
                    />
                    <Button
                        sx={{width: 300, mt: 3}}
                        variant='contained'
                        onClick={() => {
                            setClicked(true);
                            window.FileMaker
                                ? window.FileMaker.PerformScript(
                                      'trackerAtlassianApp__setJiraProject',
                                      JSON.stringify(selectedProject),
                                  )
                                : console.log('no fm', selectedProject);
                        }}
                        disabled={clicked || !selectedProject}
                    >
                        Continue
                    </Button>
                </FormControl>
            )}
        </>
    );
};

export default Home;
